'use client';

import { Typography } from '@musinsa/mds';
import type { AgreementKey } from '@musinsa/services-curator';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { PageViewTracker } from '../../../common/components';
import { useFunnelContext } from '../../context/FunnelContext';
import { AccountNameSection } from './components/AccountNameSection';
import { AgreementSection } from './components/AgreementSection';
import { useAgreementState } from './hooks/useAgreementState';

interface Props {
  onButtonStateChange?: (disabled: boolean) => void;
  submitError?: string;
  onClearError?: () => void;
  className?: string;
}

export const AccountFunnel = ({
  onButtonStateChange,
  submitError = '',
  onClearError,
  className,
}: Props) => {
  const { formValues, updateFormValues } = useFunnelContext();
  const shopTitle = formValues.shopTitle || '';
  const { agreements, toggleAgreement, isAllAgreed } = useAgreementState();

  const isFormValid = Boolean(shopTitle.trim()) && isAllAgreed;

  // 약관 동의 상태를 formValues에 반영
  useEffect(() => {
    if (isAllAgreed) {
      updateFormValues({
        agreedTerms: Object.keys(agreements).filter(
          (key) => agreements[key as keyof typeof agreements],
        ) as AgreementKey[],
      });
    } else {
      updateFormValues({ agreedTerms: [] });
    }
  }, [isAllAgreed, agreements, updateFormValues]);

  useEffect(() => {
    onButtonStateChange?.(!isFormValid);
  }, [isFormValid, onButtonStateChange]);

  const handleShopTitleChange = (value: string) => {
    if (submitError) {
      onClearError?.();
    }
    updateFormValues({ shopTitle: value });
  };

  return (
    <>
      {/* NOTE: musinsaPageName=""로 Musinsa Amplitude 발화를 skip (mss-curator에서는 AccountFunnel 진입 시 screen event 없었음) */}
      {/* TODO: 다음 배포 때 PageViewTracker 구조 개선 시 정리 필요 */}
      <PageViewTracker
        pageName="curator_signup"
        musinsaPageName=""
        snowplowProperties={{
          stage_status: 'step3_success',
        }}
      />
      <div className={twMerge('flex flex-col h-full px-4 grow pb-[72px]!', className)}>
        <div className="pt-6 pb-4">
          <div className="flex flex-col gap-1">
            <Typography variant="title18pxMed">
              사용하고자 하는
              <br />
              큐레이션 샵 계정명을 기입해주세요.
            </Typography>
            <Typography variant="body13pxReg" colorScheme="gray-600">
              큐레이션 서비스에서는 큐레이션한 상품을 전시 배치할 수 있는 고유의 샵 페이지를
              제공합니다.
            </Typography>
          </div>
        </div>

        <div className="flex-1">
          <AccountNameSection
            value={shopTitle}
            onChange={handleShopTitleChange}
            error={submitError}
          />

          <AgreementSection termsAgreements={agreements} onToggle={toggleAgreement} />
        </div>
      </div>
    </>
  );
};
